import { useState } from 'react';

interface BlogHeroProps {
  onSearch: (query: string) => void;
}

export default function BlogHero({ onSearch }: BlogHeroProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <section className="relative py-28 overflow-hidden" style={{ background: 'linear-gradient(135deg, #100D08 0%, #16120C 40%, #1E1912 70%, #28221A 100%)' }}>
      {/* Warm glowing orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-16 left-1/4 w-80 h-80 bg-gold-600/8 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-8 right-1/4 w-64 h-64 bg-gold-500/6 rounded-full blur-[80px]"></div>
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/25 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold-500/15 to-transparent"></div>
      </div>

      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #C9A440 1px, transparent 0)', backgroundSize: '40px 40px' }}>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gold-500/10 border border-gold-500/25 rounded-full mb-7">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-quill-pen-line text-gold-400"></i>
            </div>
            <span className="font-['Montserrat'] text-xs font-medium text-gold-400 tracking-[0.2em] uppercase">Blog</span>
          </div>

          {/* Title */}
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl text-white mb-6 leading-tight">
            Jornada de{' '}
            <span className="italic text-gold-400">
              Autoconhecimento
            </span>
          </h1>

          {/* Divider */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-16 h-px bg-gradient-to-r from-transparent to-gold-500/50"></div>
            <div className="w-1.5 h-1.5 rounded-full bg-gold-500/60"></div>
            <div className="w-16 h-px bg-gradient-to-l from-transparent to-gold-500/50"></div>
          </div>

          {/* Description */}
          <p className="font-['Montserrat'] text-base text-white/55 mb-10 leading-relaxed font-light tracking-wide max-w-2xl mx-auto">
            Artigos, reflexões e práticas para transformar sua vida através do autoconhecimento, gratidão e conexão com seu eu futuro.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-gold-500/15 to-gold-600/10 rounded-xl blur-lg opacity-60"></div>
              <div className="relative flex items-center bg-cream-50 border border-gold-300/40 rounded-xl overflow-hidden">
                <div className="pl-5 flex items-center justify-center w-10 h-10">
                  <i className="ri-search-line text-gold-500 text-xl"></i>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar artigos sobre autoconhecimento, gratidão..."
                  className="flex-1 px-4 py-4 text-charcoal-700 placeholder-charcoal-400/60 focus:outline-none text-sm bg-transparent font-['Montserrat']"
                />
                <button
                  type="submit"
                  className="px-7 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-white font-['Montserrat'] font-semibold text-sm hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap tracking-wide"
                >
                  Buscar
                </button>
              </div>
            </div>
          </form>

          {/* Stats row */}
          <div className="flex items-center justify-center gap-8 mt-10">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-article-line text-gold-500/60 text-sm"></i>
              </div>
              <span className="font-['Montserrat'] text-xs text-white/30 tracking-wider">Novos artigos toda semana</span>
            </div>
            <div className="w-px h-4 bg-gold-500/20"></div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-heart-3-line text-gold-500/60 text-sm"></i>
              </div>
              <span className="font-['Montserrat'] text-xs text-white/30 tracking-wider">Por Bia Holmes</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
